import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { BaseApiService } from '../../services/BaseApiService';
import { formatApiErrorForUser } from '../../utils/apiRequestError';
import { formatDate, formatDateWithoutYear } from '../../utils/date';
import { addDays, daysDifference } from '../../utils/TimeUtils';

export type BulkOrderStatus = 'order_now' | 'order_soon' | 'order_later' | 'covered';

export interface BulkNeedWeek {
  start_ymd: string;
  /** Bulk cases for open production orders that start this week. */
  scheduled_cases: number;
  /** Bulk cases for forecast production that is not scheduled yet. */
  forecast_cases: number;
  /** Scheduled bulk on each day of the week. Index 0 is start_ymd. */
  scheduled_by_day?: number[];
}

export interface BulkUsedBy {
  product_id: number;
  product_code: string;
  name: string;
  /** Bulk cases per finished case. */
  bulk_per_case: number;
  need_cases: number;
  last_year_cases: number;
  yoy_pct: number | null;
  uplift_pct: number;
}

export interface BulkTrackerRow {
  id: number;
  name: string;
  sku_number: string;
  description: string | null;
  item_kind: string;
  vendors: string[];
  cases_on_hand: number;
  expired_lot_cases: number;
  /** On hand minus expired lots. */
  usable_cases_on_hand: number;
  low_stock_threshold_cases: number;
  reorder_line_cases: number;
  ideal_order_cases: number;
  case_price: number | null;
  on_promotion: boolean;
  full_pallet_promotion: boolean;
  lead_time_days: number;
  lead_time_is_default: boolean;
  weeks: BulkNeedWeek[];
  need_cases: number;
  scheduled_cases: number;
  forecast_cases: number;
  /** Bulk cases the same season weeks used last year, before growth. */
  last_year_cases: number;
  planned_cases: number;
  peak_week_start: string | null;
  /** First day projected stock is at or below the reorder line. null = covered through the window. */
  covered_until_ymd: string | null;
  /** Can be in the past when the order is late. */
  order_by_ymd: string | null;
  status: BulkOrderStatus;
  order_cases: number;
  line_total: number | null;
  used_by: BulkUsedBy[];
}

export interface BulkTrackerResponse {
  start_ymd: string;
  end_ymd: string;
  week_starts: string[];
  reorder_line_percent: number;
  default_lead_time_days: number;
  order_soon_days: number;
  /** Extract date of the sales history used. null = no extract yet, scheduled orders only. */
  forecast_as_of: string | null;
  /** False when this user cannot open Bloom stock, so case prices and line totals are omitted. */
  prices_included: boolean;
  rows: BulkTrackerRow[];
  warnings: string[];
}

/** Client rule: vendors ship a combined order of 6 or more items totaling over $5,000. */
export const COMBINED_SHIP_MIN_ITEMS = 6;
export const COMBINED_SHIP_MIN_DOLLARS = 5000;
/** Vendors shown on one page of the bulk items table. */
export const VENDOR_PAGE_SIZE = 10;

export const ORDER_STATUS_LABELS: Record<BulkOrderStatus, string> = {
  order_now: 'Order now',
  order_soon: 'Order soon',
  order_later: 'Order later',
  covered: 'Covered',
};

export type BulkTrackerContext = Pick<
  BulkTrackerResponse,
  | 'start_ymd'
  | 'end_ymd'
  | 'reorder_line_percent'
  | 'default_lead_time_days'
  | 'order_soon_days'
  | 'prices_included'
>;

export type TrackerTile = 'order_now' | 'order_soon' | 'combined';

function round_money(value: number): number {
  return Math.round(value * 100) / 100;
}

export function format_money(value: number | null): string {
  if (value == null) return '—';
  const digits = Number.isInteger(value) ? 0 : 2;
  return value.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

export function format_cases(value: number): string {
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 }).format(value);
}

/** Bulk cases per finished case: recipe ratios under 1 keep their digits (0.24, 0.0208). */
export function format_per_case(value: number): string {
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: value < 1 ? 4 : 2 }).format(value);
}

export function count_label(count: number, word: string): string {
  return `${format_cases(count)} ${word}${count === 1 ? '' : 's'}`;
}

export function format_pct(value: number | null): string {
  if (value == null) return '—';
  return value > 0 ? `+${value}%` : `${value}%`;
}

/** "Dec 4 – Dec 12, 2026", both years when the range crosses one, one date for a one-day range. */
export function date_range_label(start_ymd: string, end_ymd: string): string {
  if (start_ymd === end_ymd) return formatDate(start_ymd);
  if (start_ymd.slice(0, 4) === end_ymd.slice(0, 4)) {
    return `${formatDateWithoutYear(start_ymd)} – ${formatDate(end_ymd)}`;
  }
  return `${formatDate(start_ymd)} – ${formatDate(end_ymd)}`;
}

export function order_by_label(order_by_ymd: string | null, today_ymd: string): string {
  if (!order_by_ymd) return '—';
  const days = daysDifference(today_ymd, order_by_ymd);
  if (days === 0) return 'Today';
  const date = formatDateWithoutYear(order_by_ymd);
  return days < 0 ? `${date} · ${count_label(-days, 'day')} late` : date;
}

export function vendor_key(name: string | null | undefined): string {
  return (name || '').trim().toLowerCase();
}

export function primary_vendor(row: Pick<BulkTrackerRow, 'vendors'>): string | null {
  const name = row.vendors[0]?.trim();
  return name || null;
}

/** Each distinct vendor on the item, in list order. Empty means the item has no vendor. */
export function listed_vendors(row: Pick<BulkTrackerRow, 'vendors'>): Array<string | null> {
  const names: string[] = [];
  const seen = new Set<string>();
  for (const raw of row.vendors) {
    const name = raw.trim();
    const key = vendor_key(name);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    names.push(name);
  }
  return names.length > 0 ? names : [null];
}

export function is_orderable(row: Pick<BulkTrackerRow, 'status' | 'order_cases'>): boolean {
  return row.status !== 'covered' && row.order_cases > 0;
}

export interface CombinedOrderCheck {
  item_count: number;
  /** Priced lines only. */
  total_dollars: number;
  missing_price_count: number;
  ships_combined: boolean;
}

export function combined_vendor_order(
  lines: Array<Pick<BulkTrackerRow, 'line_total'>>
): CombinedOrderCheck {
  let total_dollars = 0;
  let missing_price_count = 0;
  for (const line of lines) {
    if (line.line_total == null) missing_price_count += 1;
    else total_dollars += line.line_total;
  }
  total_dollars = round_money(total_dollars);
  return {
    item_count: lines.length,
    total_dollars,
    missing_price_count,
    ships_combined:
      lines.length >= COMBINED_SHIP_MIN_ITEMS && total_dollars > COMBINED_SHIP_MIN_DOLLARS,
  };
}

export interface VendorOrderGroup {
  /** Lowercase primary vendor; '' when the item has no vendor. */
  key: string;
  vendor: string | null;
  /** Order now and order soon. */
  due: BulkTrackerRow[];
  /** Reach their reorder line later in the window; adding them can make the order ship combined. */
  later: BulkTrackerRow[];
  earliest_order_by: string | null;
}

function by_order_by(a: BulkTrackerRow, b: BulkTrackerRow): number {
  const a_date = a.order_by_ymd ?? '9999-12-31';
  const b_date = b.order_by_ymd ?? '9999-12-31';
  if (a_date !== b_date) return a_date < b_date ? -1 : 1;
  return a.name.localeCompare(b.name);
}

/** One order per primary vendor that has something to order now or soon, most urgent first. */
export function group_vendor_orders(rows: BulkTrackerRow[]): VendorOrderGroup[] {
  const groups = new Map<string, VendorOrderGroup>();
  for (const row of rows) {
    if (!is_orderable(row)) continue;
    const vendor = primary_vendor(row);
    const key = vendor_key(vendor);
    let group = groups.get(key);
    if (!group) {
      group = { key, vendor, due: [], later: [], earliest_order_by: null };
      groups.set(key, group);
    }
    (row.status === 'order_later' ? group.later : group.due).push(row);
  }
  return [...groups.values()]
    .filter((group) => group.due.length > 0)
    .map((group) => {
      const due = [...group.due].sort(by_order_by);
      return {
        ...group,
        due,
        later: [...group.later].sort(by_order_by),
        earliest_order_by: due[0].order_by_ymd,
      };
    })
    .sort((a, b) => {
      const a_date = a.earliest_order_by ?? '9999-12-31';
      const b_date = b.earliest_order_by ?? '9999-12-31';
      if (a_date !== b_date) return a_date < b_date ? -1 : 1;
      return (a.vendor || '').localeCompare(b.vendor || '');
    });
}

/** Later items, soonest needed first, added until the order ships combined. None when it cannot. */
export function default_pull_forward(group: VendorOrderGroup): number[] {
  if (combined_vendor_order(group.due).ships_combined) return [];
  const added: BulkTrackerRow[] = [];
  for (const row of group.later) {
    added.push(row);
    if (combined_vendor_order([...group.due, ...added]).ships_combined) {
      return added.map((item) => item.id);
    }
  }
  return [];
}

function suggested_lines(group: VendorOrderGroup, rows: BulkTrackerRow[]): BulkTrackerRow[] {
  const ids = new Set([...group.due.map((row) => row.id), ...default_pull_forward(group)]);
  return rows.filter((row) => ids.has(row.id));
}

/** Words for the combined tile. "0 of 1" hid that the one vendor is due but short of the rule. */
function combined_tile_text(
  rows: BulkTrackerRow[],
  prices_included: boolean
): { value: string; note: string } {
  if (!prices_included) return { value: '—', note: 'Case prices hidden' };
  const groups = group_vendor_orders(rows).filter((group) => group.vendor);
  const details = groups.map((group) => {
    const lines = suggested_lines(group, rows);
    return { vendor: group.vendor as string, ...combined_vendor_order(lines) };
  });
  const shipping = details.filter((detail) => detail.ships_combined).length;
  const rule = `${COMBINED_SHIP_MIN_ITEMS} or more items over ${format_money(COMBINED_SHIP_MIN_DOLLARS)}`;
  if (details.length === 0) {
    return { value: 'None due', note: 'No named vendor has an order due now or soon.' };
  }
  if (shipping === 0 && details.length === 1) {
    const one = details[0];
    const unpriced = one.missing_price_count > 0 ? `, ${one.missing_price_count} unpriced` : '';
    return {
      value: 'Not combined',
      note: `${one.vendor} has ${count_label(one.item_count, 'item')} totaling ${format_money(
        one.total_dollars
      )}${unpriced}. A combined shipment needs ${rule}.`,
    };
  }
  if (shipping === 0) {
    return {
      value: 'None combined',
      note: `${details.length} vendors have an order due. A combined shipment needs ${rule}.`,
    };
  }
  return { value: `${shipping} of ${details.length}`, note: rule };
}

/** Row ids a summary tile refers to. Combined uses the suggested shipment, not a picked list. */
export function tile_row_ids(tile: TrackerTile, rows: BulkTrackerRow[]): number[] {
  if (tile === 'combined') {
    const ids: number[] = [];
    for (const group of group_vendor_orders(rows)) {
      if (!group.vendor) continue;
      const lines = suggested_lines(group, rows);
      if (combined_vendor_order(lines).ships_combined) ids.push(...lines.map((row) => row.id));
    }
    return ids;
  }
  return rows.filter((row) => row.status === tile).map((row) => row.id);
}

export interface VendorItemGroup {
  key: string;
  vendor: string | null;
  rows: BulkTrackerRow[];
}

export function vendor_group_label(group: Pick<VendorItemGroup, 'vendor'>): string {
  return group.vendor || 'No vendor';
}

/**
 * Every tracked item under each of its vendors. Groups follow the row order, so the vendor
 * with the most urgent item comes first. Items with no vendor share one group.
 */
export function group_rows_by_vendor(rows: BulkTrackerRow[]): VendorItemGroup[] {
  const groups = new Map<string, VendorItemGroup>();
  for (const row of rows) {
    const seen = new Set<string>();
    for (const vendor of listed_vendors(row)) {
      const key = vendor ? vendor_key(vendor) : 'no-vendor';
      if (seen.has(key)) continue;
      seen.add(key);
      let group = groups.get(key);
      if (!group) {
        group = { key, vendor, rows: [] };
        groups.set(key, group);
      }
      group.rows.push(row);
    }
  }
  return [...groups.values()];
}

/** A vendor name match keeps every SKU. A name or SKU match keeps only those SKUs. */
export function vendor_groups_for_query(rows: BulkTrackerRow[], needle: string): VendorItemGroup[] {
  const groups = group_rows_by_vendor(rows);
  if (!needle) return groups;
  return groups.flatMap((group) => {
    if ((group.vendor || '').toLowerCase().includes(needle)) return [group];
    const matched = group.rows.filter((row) =>
      [row.name, row.sku_number].some((value) => value.toLowerCase().includes(needle))
    );
    return matched.length > 0 ? [{ ...group, rows: matched }] : [];
  });
}

export interface PageSlice<T> {
  page: number;
  page_count: number;
  items: T[];
}

export function page_slice<T>(
  items: T[],
  page: number,
  page_size = VENDOR_PAGE_SIZE
): PageSlice<T> {
  const page_count = Math.max(1, Math.ceil(items.length / page_size));
  const current = Math.min(page_count, Math.max(1, Math.floor(page) || 1));
  const start = (current - 1) * page_size;
  return { page: current, page_count, items: items.slice(start, start + page_size) };
}

export interface StatusTotal {
  count: number;
  dollars: number;
  missing_price_count: number;
}

export function status_totals(rows: BulkTrackerRow[]): Record<BulkOrderStatus, StatusTotal> {
  const totals: Record<BulkOrderStatus, StatusTotal> = {
    order_now: { count: 0, dollars: 0, missing_price_count: 0 },
    order_soon: { count: 0, dollars: 0, missing_price_count: 0 },
    order_later: { count: 0, dollars: 0, missing_price_count: 0 },
    covered: { count: 0, dollars: 0, missing_price_count: 0 },
  };
  for (const row of rows) {
    const total = totals[row.status];
    total.count += 1;
    if (row.order_cases <= 0) continue;
    if (row.line_total == null) total.missing_price_count += 1;
    else total.dollars = round_money(total.dollars + row.line_total);
  }
  return totals;
}

/** Day offset from the window start as a 0–100 position; dates outside the window are clamped. */
export function window_position_pct(ymd: string, start_ymd: string, end_ymd: string): number {
  const total_days = daysDifference(start_ymd, end_ymd) + 1;
  const offset = daysDifference(start_ymd, ymd);
  return Math.min(100, Math.max(0, (offset / total_days) * 100));
}

/** Plain-language reasons behind an item's status, order-by date and order size. */
export function reorder_why(row: BulkTrackerRow, context: BulkTrackerContext): string[] {
  const lines: string[] = [];
  const expired =
    row.expired_lot_cases > 0
      ? ` (${format_cases(row.cases_on_hand)} on hand less ${count_label(
          row.expired_lot_cases,
          'case'
        )} in expired lots)`
      : '';
  lines.push(`Usable stock: ${count_label(row.usable_cases_on_hand, 'case')}${expired}.`);

  const need = `Need through ${formatDate(context.end_ymd)}: ${count_label(row.need_cases, 'case')}`;
  lines.push(
    row.need_cases > 0
      ? `${need} (${format_cases(row.scheduled_cases)} for scheduled production orders, ${format_cases(
          row.forecast_cases
        )} forecast from last year's sales).`
      : `${need}.`
  );
  if (row.last_year_cases > 0) {
    lines.push(
      `The same season weeks last year used ${count_label(
        row.last_year_cases,
        'case'
      )}. With each product's year-over-year growth the plan is ${count_label(
        row.planned_cases,
        'case'
      )}; finished stock on hand and scheduled orders cover part of that first.`
    );
  }

  lines.push(
    row.low_stock_threshold_cases > 0
      ? `Reorder line: ${count_label(row.reorder_line_cases, 'case')} (${
          context.reorder_line_percent
        }% of the ${format_cases(row.low_stock_threshold_cases)}-case reorder threshold).`
      : 'No reorder threshold is set, so the line is running out (0 cases). Set one on Bloom stock.'
  );

  if (!row.covered_until_ymd) {
    lines.push(`Stock stays above the line through ${formatDate(context.end_ymd)}.`);
    return lines;
  }
  lines.push(
    row.covered_until_ymd <= context.start_ymd
      ? 'Stock is at or below the line now.'
      : `Stock reaches the line on ${formatDate(row.covered_until_ymd)}.`
  );

  const lead = row.lead_time_is_default
    ? `Lead time is not set, so the ${context.default_lead_time_days}-day default is used`
    : `Lead time is ${count_label(row.lead_time_days, 'day')}`;
  lines.push(`${lead}: order by ${order_by_label(row.order_by_ymd, context.start_ymd)}.`);

  if (row.order_cases > 0) {
    const normal =
      row.ideal_order_cases > 0
        ? ` (${row.order_cases / row.ideal_order_cases} normal order${
            row.order_cases / row.ideal_order_cases === 1 ? '' : 's'
          } of ${format_cases(row.ideal_order_cases)})`
        : '';
    const price = !context.prices_included
      ? ''
      : row.line_total == null
        ? ' Add a case price on Bloom stock to price it.'
        : ` At ${format_money(row.case_price)} a case: ${format_money(row.line_total)}.`;
    lines.push(
      `Order ${count_label(row.order_cases, 'case')}${normal} to cover the need and stay above the line.${price}`
    );
  }
  return lines;
}

/** Short hover text for each tracker number, using this row's own figures. */
export function row_value_tips(
  row: BulkTrackerRow,
  context: BulkTrackerContext
): {
  sku: string;
  usable: string;
  need: string;
  runway: string;
  covered_until: string;
  order_by: string;
  status: string;
  order: string;
} {
  const usable =
    row.expired_lot_cases > 0
      ? `${format_cases(row.cases_on_hand)} on hand minus ${format_cases(row.expired_lot_cases)} in expired lots.`
      : `${format_cases(row.cases_on_hand)} on hand. No expired lots.`;

  const need = `${format_cases(row.scheduled_cases)} from pending and in-progress production orders, plus ${format_cases(row.forecast_cases)} forecast. The forecast is last year's matching weeks, raised by each finished product's last 12 months versus the 12 months before. Finished stock and scheduled orders are covered first.`;

  const line =
    row.low_stock_threshold_cases > 0
      ? `the reorder line (${context.reorder_line_percent}% of the ${format_cases(row.low_stock_threshold_cases)}-case threshold = ${format_cases(row.reorder_line_cases)})`
      : 'zero cases, because no reorder threshold is set';

  const covered_until = row.covered_until_ymd
    ? `First day usable stock falls to ${line}: ${formatDate(row.covered_until_ymd)}. The 3-month need is the whole window; stock only drops on the days it is used.`
    : `Usable stock stays above ${line} through ${formatDate(context.end_ymd)}.`;

  const lead = row.lead_time_is_default
    ? `the ${context.default_lead_time_days}-day default, because Bloom stock has no lead time`
    : `${count_label(row.lead_time_days, 'day')} saved on Bloom stock`;
  const order_by = row.order_by_ymd
    ? `Covered-until minus ${lead}: ${order_by_label(row.order_by_ymd, context.start_ymd)}.`
    : 'No order-by date. Stock stays above the line for the whole window.';

  const days = row.order_by_ymd ? daysDifference(context.start_ymd, row.order_by_ymd) : null;
  const status =
    row.status === 'covered'
      ? 'Covered: stock stays above the line through the window.'
      : days != null && days <= 0
        ? 'Order now: the order-by date is today or already past.'
        : days != null && days <= context.order_soon_days
          ? `Order soon: the order-by date is ${count_label(days, 'day')} away, inside the ${context.order_soon_days}-day window.`
          : `Order later: the order-by date is ${count_label(days ?? 0, 'day')} away, after the ${context.order_soon_days}-day window.`;

  const normal =
    row.ideal_order_cases > 0
      ? ` Rounded up to whole normal orders of ${format_cases(row.ideal_order_cases)}.`
      : '';
  const price =
    !context.prices_included
      ? ' Case price is hidden.'
      : row.case_price == null
        ? ' No case price on Bloom stock.'
        : ` ${format_cases(row.order_cases)} × ${format_money(row.case_price)} = ${format_money(row.line_total)}.`;
  const order =
    row.order_cases > 0
      ? `Cases to cover the ${format_cases(row.need_cases)} need, finish above the reorder line, and reach the full threshold.${normal}${price}`
      : 'Nothing to order. Stock covers the window.';

  const runway = row.covered_until_ymd
    ? `Bar runs until ${formatDateWithoutYear(row.covered_until_ymd)}, when stock hits the line. The tick is the order-by date.`
    : `Bar fills the window. Stock stays above the line through ${formatDateWithoutYear(context.end_ymd)}.`;

  return {
    sku: `${row.sku_number}. Listed under each vendor saved on this Bloom stock item.`,
    usable,
    need,
    runway,
    covered_until,
    order_by,
    status,
    order,
  };
}

const ITEM_COLUMN_COUNT = 9;

const BulkStatusPill: React.FC<{ status: BulkOrderStatus }> = ({ status }) => (
  <span className={`bulk-status bulk-status--${status}`}>{ORDER_STATUS_LABELS[status]}</span>
);

const BulkPromotionBadges: React.FC<{
  row: Pick<BulkTrackerRow, 'on_promotion' | 'full_pallet_promotion'>;
}> = ({ row }) => {
  if (!row.on_promotion && !row.full_pallet_promotion) return null;
  return (
    <span className="bulk-promo-list">
      {row.on_promotion && <span className="bulk-promo">Discount</span>}
      {row.full_pallet_promotion && <span className="bulk-promo">Full pallet</span>}
    </span>
  );
};

const BulkItemName: React.FC<{
  row: Pick<BulkTrackerRow, 'name' | 'sku_number' | 'on_promotion' | 'full_pallet_promotion'>;
}> = ({ row }) => (
  <>
    <span className="bulk-items__name">{row.name}</span>
    <span className="bulk-items__sku">
      {row.sku_number}
      <BulkPromotionBadges row={row} />
    </span>
  </>
);

function ValueTip({ tip, children }: { tip: string; children: React.ReactNode }) {
  const anchor = useRef<HTMLSpanElement>(null);
  const [box, set_box] = useState<{ top: number; left: number; flip: boolean } | null>(null);

  const show = () => {
    const node = anchor.current;
    if (!node) return;
    const rect = node.getBoundingClientRect();
    const width = 240;
    const left = Math.max(8, Math.min(rect.left, window.innerWidth - width - 12));
    const flip = rect.bottom + 148 > window.innerHeight;
    set_box({ top: flip ? rect.top - 8 : rect.bottom + 8, left, flip });
  };

  return (
    <span
      className="bulk-tip"
      ref={anchor}
      tabIndex={0}
      onMouseEnter={show}
      onMouseLeave={() => set_box(null)}
      onFocus={show}
      onBlur={() => set_box(null)}
    >
      {children}
      {box &&
        createPortal(
          <span
            className={`bulk-tip__bubble${box.flip ? ' bulk-tip__bubble--above' : ''}`}
            style={{ top: box.top, left: box.left }}
            role="tooltip"
          >
            {tip}
          </span>,
          document.body
        )}
    </span>
  );
}

function RunwayBar({ row, context }: { row: BulkTrackerRow; context: BulkTrackerContext }) {
  const covered_pct = row.covered_until_ymd
    ? window_position_pct(row.covered_until_ymd, context.start_ymd, context.end_ymd)
    : 100;
  const order_by_pct =
    row.order_by_ymd && row.order_by_ymd >= context.start_ymd
      ? window_position_pct(row.order_by_ymd, context.start_ymd, context.end_ymd)
      : null;
  return (
    <span className={`bulk-runway bulk-runway--${row.status}`}>
      <span className="bulk-runway__fill" style={{ width: `${covered_pct}%` }} />
      {order_by_pct != null && (
        <span className="bulk-runway__marker" style={{ left: `${order_by_pct}%` }} />
      )}
    </span>
  );
}

export const BulkItemDetail: React.FC<{
  row: BulkTrackerRow;
  context: BulkTrackerContext;
}> = ({ row, context }) => (
  <div className="bulk-detail">
    <h4 className="bulk-detail__heading">Why</h4>
    <ul className="bulk-detail__why">
      {reorder_why(row, context).map((line) => (
        <li key={line}>{line}</li>
      ))}
    </ul>
    <h4 className="bulk-detail__heading">Used by</h4>
    {row.used_by.length === 0 ? (
      <p className="forecast-page__recs-empty">
        No product recipe or packaging uses this item in the next 3 months.
      </p>
    ) : (
      <div className="forecast-bulk-tracker__table-wrap">
        <table className="forecast-bulk-tracker__table">
          <thead>
            <tr>
              <th>Product</th>
              <th title="Bulk cases per finished case">Per case</th>
              <th title="Bulk cases the same season weeks used last year">Last year</th>
              <th>YoY</th>
              <th title="Year-over-year growth used for the plan; never below 0">Growth used</th>
              <th>3-month need</th>
            </tr>
          </thead>
          <tbody>
            {row.used_by.map((product) => (
              <tr key={product.product_id}>
                <td>
                  <span className="bulk-items__name">{product.product_code}</span>
                  <span className="bulk-items__sku">{product.name}</span>
                </td>
                <td>{format_per_case(product.bulk_per_case)}</td>
                <td>{format_cases(product.last_year_cases)}</td>
                <td>{format_pct(product.yoy_pct)}</td>
                <td>{format_pct(product.uplift_pct)}</td>
                <td>{format_cases(product.need_cases)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

interface ItemsTableProps {
  rows: BulkTrackerRow[];
  context: BulkTrackerContext;
  /** null shows every vendor collapsed. A list opens those vendors and highlights those rows. */
  highlight_ids: number[] | null;
}

export const BulkItemsTable: React.FC<ItemsTableProps> = ({ rows, context, highlight_ids }) => {
  const [query, set_query] = useState('');
  const [page, set_page] = useState(1);
  const [manual_open, set_manual_open] = useState<Set<string>>(new Set());
  const [closed_while_highlight, set_closed] = useState<Set<string>>(new Set());
  const [expanded_key, set_expanded_key] = useState<string | null>(null);

  const highlight_key = highlight_ids ? highlight_ids.join(',') : '';
  const highlighting = highlight_ids != null;
  const hits = useMemo(() => new Set(highlight_ids ?? []), [highlight_ids]);
  const needle = query.trim().toLowerCase();
  const force_open = highlighting || needle.length > 0;

  useEffect(() => {
    set_page(1);
    set_closed(new Set());
  }, [highlight_key, needle]);

  const groups = vendor_groups_for_query(rows, needle).filter(
    (group) => !highlighting || group.rows.some((row) => hits.has(row.id))
  );
  const sliced = page_slice(groups, page);

  const group_open = (key: string) =>
    force_open ? !closed_while_highlight.has(key) : manual_open.has(key);

  const toggle_group = (key: string) => {
    const update = (current: Set<string>) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    };
    if (force_open) set_closed(update);
    else set_manual_open(update);
  };

  return (
    <div className="bulk-items">
      <div className="bulk-items__controls">
        <input
          className="forecast-anomaly__search bulk-items__search"
          value={query}
          onChange={(event) => {
            set_query(event.target.value);
            set_page(1);
          }}
          placeholder="Filter name, SKU or vendor"
          aria-label="Filter bulk items by name, SKU or vendor"
        />
      </div>
      {sliced.items.length === 0 ? (
        <p className="forecast-page__empty">
          {highlighting ? 'No items in this selection.' : 'No bulk items match.'}
        </p>
      ) : (
        <div className="forecast-bulk-tracker__table-wrap">
          <table className="forecast-bulk-tracker__table bulk-items__table">
            <thead>
              <tr>
                <th>Bulk SKU</th>
                <th title="On hand minus expired lots">Usable</th>
                <th title={`Bulk cases needed through ${formatDateWithoutYear(context.end_ymd)}`}>
                  3-month need
                </th>
                <th title="Green: above the reorder line. Tick: order-by date.">Runway</th>
                <th>Covered until</th>
                <th>Order by</th>
                <th>Status</th>
                <th>Order</th>
                <th aria-label="Details" />
              </tr>
            </thead>
            <tbody>
              {sliced.items.map((group) => {
                const open = group_open(group.key);
                return (
                  <Fragment key={group.key}>
                    <tr className="bulk-items__vendor">
                      <td colSpan={ITEM_COLUMN_COUNT}>
                        <button
                          type="button"
                          className="bulk-items__vendor-toggle"
                          aria-expanded={open}
                          onClick={() => toggle_group(group.key)}
                        >
                          <span aria-hidden="true">{open ? '▾' : '▸'}</span>
                          <span>{vendor_group_label(group)}</span>
                          <span className="bulk-items__sku">
                            {count_label(group.rows.length, 'item')}
                          </span>
                        </button>
                      </td>
                    </tr>
                    {group.rows.map((row) => {
                      const row_key = `${group.key}:${row.id}`;
                      const expanded = open && expanded_key === row_key;
                      const toggle = () => set_expanded_key(expanded ? null : row_key);
                      const hit = hits.has(row.id);
                      const tips = row_value_tips(row, context);
                      return (
                        <Fragment key={row_key}>
                          <tr
                            className={`bulk-items__row${expanded ? ' bulk-items__row--open' : ''}${
                              hit ? ' bulk-items__row--hit' : ''
                            }${open ? '' : ' bulk-items__row--collapsed'}`}
                            aria-hidden={open ? undefined : true}
                            inert={open ? undefined : true}
                            onClick={open ? toggle : undefined}
                          >
                            <td>
                              <ValueTip tip={tips.sku}>
                                <BulkItemName row={row} />
                              </ValueTip>
                            </td>
                            <td>
                              <ValueTip tip={tips.usable}>
                                {format_cases(row.usable_cases_on_hand)}
                                {row.expired_lot_cases > 0 && (
                                  <span className="bulk-items__sku">
                                    {format_cases(row.expired_lot_cases)} expired
                                  </span>
                                )}
                              </ValueTip>
                            </td>
                            <td>
                              <ValueTip tip={tips.need}>{format_cases(row.need_cases)}</ValueTip>
                            </td>
                            <td>
                              <ValueTip tip={tips.runway}>
                                <RunwayBar row={row} context={context} />
                              </ValueTip>
                            </td>
                            <td>
                              <ValueTip tip={tips.covered_until}>
                                {row.covered_until_ymd
                                  ? formatDateWithoutYear(row.covered_until_ymd)
                                  : 'Through window'}
                              </ValueTip>
                            </td>
                            <td>
                              <ValueTip tip={tips.order_by}>
                                {order_by_label(row.order_by_ymd, context.start_ymd)}
                                {row.order_by_ymd && row.lead_time_is_default && (
                                  <span className="bulk-items__sku">default lead time</span>
                                )}
                              </ValueTip>
                            </td>
                            <td>
                              <ValueTip tip={tips.status}>
                                <BulkStatusPill status={row.status} />
                              </ValueTip>
                            </td>
                            <td>
                              <ValueTip tip={tips.order}>
                              {row.order_cases > 0 ? (
                                <>
                                  {format_cases(row.order_cases)} cases
                                  {context.prices_included && (
                                    <span className="bulk-items__sku">
                                      {format_money(row.line_total)}
                                    </span>
                                  )}
                                </>
                              ) : (
                                '—'
                              )}
                              </ValueTip>
                            </td>
                            <td>
                              <button
                                type="button"
                                className="bulk-items__expand"
                                aria-expanded={expanded}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  toggle();
                                }}
                              >
                                {expanded ? 'Hide' : 'Why'}
                              </button>
                            </td>
                          </tr>
                          {expanded && (
                            <tr className="bulk-items__detail-row">
                              <td colSpan={ITEM_COLUMN_COUNT}>
                                <BulkItemDetail row={row} context={context} />
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {sliced.page_count > 1 && (
        <div className="bulk-items__pager">
          <button
            type="button"
            disabled={sliced.page <= 1}
            onClick={() => set_page(sliced.page - 1)}
          >
            Previous
          </button>
          <span>
            Page {sliced.page} of {sliced.page_count}
          </span>
          <button
            type="button"
            disabled={sliced.page >= sliced.page_count}
            onClick={() => set_page(sliced.page + 1)}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

const TRACKER_CSS = `
.forecast-bulk-tracker__help {
  margin: 0.35rem 0 0;
  color: #4b5563;
  font-size: 0.9rem;
  max-width: 60rem;
}

.forecast-bulk-tracker {
  max-width: 100%;
  min-width: 0;
  contain: inline-size;
}

.forecast-bulk-tracker__table-wrap {
  overflow-x: auto;
  max-width: 100%;
  min-width: 0;
  margin-top: 0.75rem;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
}

.forecast-bulk-tracker__table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}

.bulk-items__row--collapsed {
  visibility: collapse;
}

.bulk-tip {
  display: inline;
  cursor: help;
  border-bottom: 1px dotted #d1d5db;
}

.bulk-tip__bubble {
  position: fixed;
  z-index: 80;
  width: 15rem;
  max-width: calc(100vw - 1.5rem);
  padding: 0.55rem 0.7rem;
  background: #fff;
  color: #111827;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  box-shadow: 0 8px 24px rgba(17, 24, 39, 0.12);
  font-size: 0.8125rem;
  font-weight: 400;
  line-height: 1.45;
  letter-spacing: 0;
  text-transform: none;
  white-space: normal;
  overflow-wrap: break-word;
  pointer-events: none;
}

.bulk-tip__bubble--above {
  transform: translateY(-100%);
}

.forecast-bulk-tracker__table th,
.forecast-bulk-tracker__table td {
  padding: 0.5rem 0.65rem;
  text-align: left;
  border-bottom: 1px solid #e5e7eb;
  white-space: nowrap;
  vertical-align: top;
}

.forecast-bulk-tracker__table th {
  background: #f8fafc;
  color: #374151;
  font-weight: 600;
}

.bulk-tracker__subhead {
  margin: 1.5rem 0 0;
  font-size: 1rem;
  color: #111827;
}

.bulk-tracker__warnings {
  margin: 0.75rem 0 0;
  padding: 0.6rem 0.85rem 0.6rem 1.75rem;
  background: #fffbeb;
  border: 1px solid #fcd34d;
  border-radius: 6px;
  color: #92400e;
  font-size: 0.875rem;
}

.bulk-tracker__tiles {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.75rem;
  margin-top: 0.85rem;
}

.bulk-tracker__tile {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  width: 100%;
  padding: 0.7rem 0.85rem;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-left-width: 4px;
  border-radius: 6px;
  font: inherit;
  text-align: left;
  cursor: pointer;
}

.bulk-tracker__tile:hover {
  background: #f8fafc;
}

.bulk-tracker__tile--active {
  background: #eff6ff;
  box-shadow: inset 0 0 0 2px #2563eb;
}

.bulk-tracker__tile--now {
  border-left-color: #dc2626;
}

.bulk-tracker__tile--soon {
  border-left-color: #f59e0b;
}

.bulk-tracker__tile--combined {
  border-left-color: #2563eb;
}

.bulk-tracker__tile-label {
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #6b7280;
}

.bulk-tracker__tile-value {
  font-size: 1.35rem;
  font-weight: 700;
  color: #111827;
}

.bulk-tracker__tile-note {
  font-size: 0.8rem;
  color: #4b5563;
  white-space: normal;
}

.bulk-status {
  display: inline-block;
  padding: 0.1rem 0.5rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 700;
  white-space: nowrap;
}

.bulk-status--order_now {
  background: #fee2e2;
  color: #991b1b;
}

.bulk-status--order_soon {
  background: #fef3c7;
  color: #92400e;
}

.bulk-status--order_later {
  background: #e0f2fe;
  color: #075985;
}

.bulk-status--covered {
  background: #dcfce7;
  color: #166534;
}

.bulk-promo-list {
  display: inline-flex;
  gap: 0.25rem;
  margin-left: 0.35rem;
}

.bulk-promo {
  padding: 0 0.4rem;
  border-radius: 4px;
  background: #f3e8ff;
  color: #6b21a8;
  font-size: 0.7rem;
  font-weight: 700;
}

.bulk-items__controls {
  display: flex;
  justify-content: flex-end;
  margin-top: 0.75rem;
}

.bulk-items__search {
  width: 16rem;
  margin-bottom: 0;
}

.bulk-items__table th:first-child,
.bulk-items__table td:first-child {
  min-width: 12rem;
  white-space: normal;
  overflow-wrap: break-word;
}

.bulk-items__vendor > td {
  background: #f1f5f9;
  overflow: visible;
  text-overflow: clip;
  white-space: normal;
}

.bulk-items__vendor-toggle {
  display: inline-flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 0.45rem;
  max-width: 100%;
  padding: 0;
  border: 0;
  background: none;
  color: #111827;
  font: inherit;
  font-weight: 700;
  white-space: normal;
  cursor: pointer;
}

.bulk-items__row {
  cursor: pointer;
}

.bulk-items__row:hover td {
  background: #f8fafc;
}

.bulk-items__row--open td {
  background: #eff6ff;
}

.bulk-items__row--hit td {
  background: #fef3c7;
}

.bulk-items__name {
  display: block;
  font-weight: 600;
  color: #111827;
}

.bulk-items__sku {
  display: block;
  font-size: 0.75rem;
  font-weight: 400;
  color: #6b7280;
}

.bulk-items__expand {
  padding: 0.15rem 0.5rem;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  background: #fff;
  font-size: 0.75rem;
  cursor: pointer;
}

.bulk-items__detail-row > td {
  padding: 0.85rem;
  background: #f8fafc;
  overflow: visible;
  text-overflow: clip;
  white-space: normal;
}

.bulk-detail {
  max-width: 100%;
  overflow-x: auto;
}

.bulk-items__pager {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-top: 0.75rem;
  font-size: 0.875rem;
}

.bulk-items__pager button {
  padding: 0.25rem 0.7rem;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
}

.bulk-items__pager button:disabled {
  color: #9ca3af;
  cursor: default;
}

.bulk-runway {
  position: relative;
  display: inline-block;
  width: 7rem;
  max-width: 100%;
  height: 0.6rem;
  margin-top: 0.35rem;
  background: #e5e7eb;
  border-radius: 999px;
  overflow: hidden;
}

.bulk-runway__fill {
  position: absolute;
  inset: 0 auto 0 0;
  background: #22c55e;
}

.bulk-runway--order_now .bulk-runway__fill {
  background: #ef4444;
}

.bulk-runway--order_soon .bulk-runway__fill {
  background: #f59e0b;
}

.bulk-runway--order_later .bulk-runway__fill {
  background: #38bdf8;
}

.bulk-runway__marker {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 2px;
  background: #111827;
}

.bulk-detail__heading {
  margin: 0 0 0.35rem;
  font-size: 0.85rem;
  color: #111827;
}

.bulk-detail__why {
  margin: 0 0 1rem;
  padding-left: 1.1rem;
  font-size: 0.85rem;
  line-height: 1.45;
  color: #1f2937;
}

@media (max-width: 1100px) {
  .bulk-tracker__tiles {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
`;

class BulkTrackerApi extends BaseApiService {
  async load(): Promise<BulkTrackerResponse> {
    const payload = await this.get<{
      success: boolean;
      data: BulkTrackerResponse;
      message?: string;
    }>('/reports/production-forecast/bulk-tracker');
    if (payload?.success && payload.data) return payload.data;
    throw new Error(payload?.message || 'Failed to load the bulk tracker');
  }
}

const bulk_tracker_api = new BulkTrackerApi();

function money_note(total: StatusTotal, prices_included: boolean): string {
  if (!prices_included) return 'Case prices hidden';
  if (total.count === 0) return 'Nothing';
  const unpriced = total.missing_price_count > 0 ? ` + ${total.missing_price_count} unpriced` : '';
  return `${format_money(total.dollars)} to order${unpriced}`;
}

function SummaryTile({
  tone,
  label,
  value,
  note,
  pressed,
  onClick,
}: {
  tone: string;
  label: string;
  value: string;
  note: string;
  pressed: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={`bulk-tracker__tile bulk-tracker__tile--${tone}${
        pressed ? ' bulk-tracker__tile--active' : ''
      }`}
      aria-pressed={pressed}
      onClick={onClick}
    >
      <span className="bulk-tracker__tile-label">{label}</span>
      <span className="bulk-tracker__tile-value">{value}</span>
      <span className="bulk-tracker__tile-note">{note}</span>
    </button>
  );
}

const ForecastBulkTracker: React.FC<{ reload_key?: number }> = ({ reload_key = 0 }) => {
  const [data, set_data] = useState<BulkTrackerResponse | null>(null);
  const [loading, set_loading] = useState(true);
  const [error, set_error] = useState<string | null>(null);
  const [tile, set_tile] = useState<TrackerTile | null>(null);
  const table_ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    set_loading(true);
    set_error(null);
    bulk_tracker_api
      .load()
      .then((result) => {
        if (!cancelled) set_data(result);
      })
      .catch((err: unknown) => {
        if (!cancelled) set_error(formatApiErrorForUser(err));
      })
      .finally(() => {
        if (!cancelled) set_loading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reload_key]);

  useEffect(() => {
    if (!tile) return;
    table_ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [tile]);

  const rows = useMemo(() => data?.rows ?? [], [data]);
  const totals = useMemo(() => status_totals(rows), [rows]);
  const combined_text = useMemo(
    () => combined_tile_text(rows, Boolean(data?.prices_included)),
    [rows, data?.prices_included]
  );
  const highlight_ids = useMemo(() => (tile ? tile_row_ids(tile, rows) : null), [tile, rows]);

  const select_tile = (next: TrackerTile) => {
    set_tile((current) => (current === next ? null : next));
  };

  const context: BulkTrackerContext | null = data
    ? {
        start_ymd: data.start_ymd,
        end_ymd: data.end_ymd,
        reorder_line_percent: data.reorder_line_percent,
        default_lead_time_days: data.default_lead_time_days,
        order_soon_days: data.order_soon_days,
        prices_included: data.prices_included,
      }
    : null;

  return (
    <section className="forecast-page__section forecast-bulk-tracker">
      <style>{TRACKER_CSS}</style>
      <h2 className="forecast-page__row-title">Bulk tracker: next 3 months</h2>
      {data && (
        <p className="forecast-bulk-tracker__help">
          {date_range_label(data.start_ymd, data.end_ymd)}. Bulk need = scheduled production orders
          + last year&apos;s sales for the same season weeks, raised by each product&apos;s
          year-over-year growth (never below last year), less finished stock on hand. An item needs
          a vendor order when usable stock would fall to {data.reorder_line_percent}% of its reorder
          threshold; order by that date minus its lead time.
          {data.forecast_as_of
            ? ` Sales history from the ${formatDate(data.forecast_as_of)} extract.`
            : ''}
        </p>
      )}
      {loading && data && <p className="forecast-page__empty">Updating…</p>}
      {data && !data.prices_included && (
        <p className="forecast-bulk-tracker__help">
          Case prices are hidden. Bloom stock access is required to see them and the{' '}
          {format_money(COMBINED_SHIP_MIN_DOLLARS)} combined-shipment check.
        </p>
      )}
      {error && <p className="forecast-page__error">{error}</p>}
      {loading && !data && <p className="forecast-page__empty">Loading bulk tracker…</p>}
      {data && context && (
        <>
          {data.warnings.length > 0 && (
            <ul className="bulk-tracker__warnings">
              {data.warnings.map((warning) => (
                <li key={warning}>{warning}</li>
              ))}
            </ul>
          )}
          {rows.length === 0 ? (
            <p className="forecast-page__empty">
              No tracked Bloom stock items yet. Add items on the Bloom stock page.
            </p>
          ) : (
            <>
              <div className="bulk-tracker__tiles">
                <SummaryTile
                  tone="now"
                  label="Order now"
                  value={count_label(totals.order_now.count, 'item')}
                  note={money_note(totals.order_now, data.prices_included)}
                  pressed={tile === 'order_now'}
                  onClick={() => select_tile('order_now')}
                />
                <SummaryTile
                  tone="soon"
                  label={`Order by ${formatDate(addDays(data.start_ymd, data.order_soon_days))}`}
                  value={count_label(totals.order_soon.count, 'item')}
                  note={money_note(totals.order_soon, data.prices_included)}
                  pressed={tile === 'order_soon'}
                  onClick={() => select_tile('order_soon')}
                />
                <SummaryTile
                  tone="combined"
                  label="Vendor orders shipping combined"
                  value={combined_text.value}
                  note={combined_text.note}
                  pressed={tile === 'combined'}
                  onClick={() => select_tile('combined')}
                />
              </div>

              <div ref={table_ref}>
                <h3 className="bulk-tracker__subhead">All bulk items</h3>
                <p className="forecast-bulk-tracker__help">
                  Grouped by vendor. A tile opens the vendors that have those items and highlights
                  the rows.
                </p>
                <BulkItemsTable rows={rows} context={context} highlight_ids={highlight_ids} />
              </div>
            </>
          )}
        </>
      )}
    </section>
  );
};

export default ForecastBulkTracker;
